import Vue from 'vue';
import Router, { type RouteConfig, type RawLocation } from 'vue-router';

import { env } from '@/services/env.service';
import { isBrowser } from '@odin/utils';
import config from '@/services/config.service';
import { logError } from '@/services/logger.service';
import { CF_USECASE } from '@/components/contactForm/const.enum';
import { CAMPAIGN_ROUTES, ROUTENAMES } from '@/consts/const.enum';

import OrderLayout from '@/views/OrderLayout.vue';
import Component from 'vue-class-component';
import {
  isInIframe,
  notifyParentAboutUrlChange,
} from '@/utilities/iframeCommunication';
import { insertInstaWpCartItem } from '@/services/cart/cart-api.service';
const ContactForm = () => import('./views/ContactForm.vue');

Vue.use(Router);

Component.registerHooks([
  'beforeRouteEnter',
  'beforeRouteLeave',
  'beforeRouteUpdate', // for vue-router 2.2+
]);

export const routes: RouteConfig[] = [
  {
    name: ROUTENAMES.DOMAIN_CHECKER,
    path: '/' + ROUTENAMES.DOMAIN_CHECKER,
    meta: { layout: OrderLayout },
    component: () => import('./views/DomainCheckerView.vue'),
  },
  {
    name: ROUTENAMES.THANK_YOU,
    path: '/' + ROUTENAMES.THANK_YOU,
    component: () => import('./views/ThankYou.vue'),
  },
  {
    name: ROUTENAMES.SERVER_FORM,
    path: '/' + ROUTENAMES.SERVER_FORM,
    component: ContactForm,
    props: {
      usecase: CF_USECASE.SERVER,
    },
  },
  {
    name: ROUTENAMES.MONITORING_FORM,
    path: '/' + ROUTENAMES.MONITORING_FORM,
    component: ContactForm,
    props: {
      usecase: CF_USECASE.MONITORING,
    },
  },
  {
    name: ROUTENAMES.MANAGEMENT_FORM,
    path: '/' + ROUTENAMES.MANAGEMENT_FORM,
    component: ContactForm,
    props: {
      usecase: CF_USECASE.MANAGEMENT,
    },
  },
  {
    name: ROUTENAMES.CAMPAIGN,
    path: '/' + ROUTENAMES.CAMPAIGN,
    component: () => import('./views/AutoInsertView.vue'),
    children: [
      {
        name: ROUTENAMES.CAMPAIGN + '_' + CAMPAIGN_ROUTES.INSTA_WP,
        path: CAMPAIGN_ROUTES.INSTA_WP,
        component: () => import('./views/AutoInsertView.vue'),
        meta: {
          autoInsert: {
            productName: 'InstaWP',
            callbackFn: () => insertInstaWpCartItem(),
          },
        },
      },
    ],
  },
  {
    name: ROUTENAMES.OPTIONAL,
    path: '/' + ROUTENAMES.OPTIONAL,
    component: () => import('./views/OptionalStep.vue'),
  },
  {
    path: '/' + ROUTENAMES.ORDER,
    component: OrderLayout,
    meta: { layout: OrderLayout },
    children: [
      {
        path: '',
        name: ROUTENAMES.ORDER,
        component: () => import('./views/OrderView.vue'),
      },
    ],
  },
  {
    path: '*',
    name: '404',
    component: () => import('@/components/Page404.vue'),
  },
];

export const router = new Router({
  mode: 'history',
  base: (isBrowser && config.baseUrl) || env.VITE_BASE_URL,
  routes,
});

if (isInIframe()) {
  router.beforeEach((to, from, next) => {
    notifyParentAboutUrlChange(to.fullPath);
    next();
  });
}

export function safePush(route: RawLocation) {
  return safeRouterMethod('push', route);
}

export function safeReplace(route: RawLocation) {
  return safeRouterMethod('replace', route);
}

function safeRouterMethod(method: 'push' | 'replace', route: RawLocation) {
  return router[method](route).catch((err) => {
    // Ignore the vuex err regarding  navigating to the page they are already on.
    if (
      err.name !== 'NavigationDuplicated' &&
      !err.message.includes(
        'Avoided redundant navigation to current location'
      ) &&
      !err.message.includes('via a navigation guard') &&
      !err.message.includes('Navigation cancelled')
    ) {
      // But print any other errors to the console
      logError(err);
      return Promise.reject(err);
    }
  });
}

// TODO: don't allow user to go to another route if store.state.domainChecker.cartItemsLoading.length > 0

export default router;

export const useRouter = () => router;
